import React, { useEffect, useRef, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContentText,
  DialogContent,
  CircularProgress,
} from "@mui/material";

import { UploadFile } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { renderAsync as renderDocxAsync } from "docx-preview";
import { createCustomerPanelUi, CUSTOMER_PANEL_ACCENTS } from "./customerPanelStyles";
import { compareSeasonNames } from "../Estimates/estimateSeasonSort";
import {
  CUSTOMER_DOC_FOLDERS,
  UNASSIGNED_SEASON,
  catalogSeasonsNotInFolders,
  countByFolder,
  docsInFolder,
  docsInSeason,
  getFolderById,
  uniqueSeasonFolders,
} from "./customerDocsFolders";

import {
  getCustomerDocuments,
  uploadCustomerDocument,
  deleteCustomerDocument,
  updateCustomerDocumentName,
  downloadCustomerDocument,
  fetchCustomerDocumentBlob,
} from "../../api/Customer/customerDocsApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";

const isImageFile = (name) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(name || ""));

const isPdfFile = (name) => /\.pdf$/i.test(String(name || ""));

const isDocxFile = (name) => /\.docx$/i.test(String(name || ""));

const isExcelFile = (name) => /\.xlsx?$/i.test(String(name || ""));

const pickNameWithExtension = (...names) => {
  const list = names.map((n) => String(n || "").trim()).filter(Boolean);
  return (
    list.find((n) => /\.[A-Za-z0-9]{2,8}$/.test(n)) || list[0] || ""
  );
};

const getPreviewType = (filePath, displayName) => {
  const name = pickNameWithExtension(filePath, displayName);
  if (isImageFile(name)) return "image";
  if (isPdfFile(name)) return "pdf";
  if (isDocxFile(name)) return "docx";
  if (isExcelFile(name)) return "excel";
  return "other";
};

const isDocumentPreview = (type) =>
  type === "pdf" || type === "docx" || type === "excel";

const resolveDownloadName = (displayName, filePath) => {
  const stored = String(filePath || "").split("/").pop() || "";
  const storedExt = stored.includes(".")
    ? stored.slice(stored.lastIndexOf("."))
    : "";
  const name = String(displayName || "").trim() || "documento";
  if (storedExt && !/\.[A-Za-z0-9]{2,8}$/.test(name)) {
    return `${name}${storedExt}`;
  }
  return name;
};

const CustomerDocs = ({ customerId, COLUMN_WIDTH }) => {
  const [documentos, setDocumentos] = useState([]);
  const [nombreDocumento, setNombreDocumento] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [fechaCaducidad, setFechaCaducidad] = useState("");
  const [openFolder, setOpenFolder] = useState(null);
  const [openTemporada, setOpenTemporada] = useState(null);
  const [temporadas, setTemporadas] = useState([]);
  const [seasonToOpen, setSeasonToOpen] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [confirmDownload, setConfirmDownload] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [excelSheets, setExcelSheets] = useState([]);
  const [excelSheetName, setExcelSheetName] = useState("");
  const [excelHtml, setExcelHtml] = useState("");
  const [officeBlob, setOfficeBlob] = useState(null);
  const docxContainerRef = useRef(null);
  const excelWorkbookRef = useRef(null);

  // drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  const theme = useTheme();
  const accent = CUSTOMER_PANEL_ACCENTS.docs;
  const ui = createCustomerPanelUi(theme, accent);

  const { t } = useTranslation();
  const panelSx = {
    ...ui.panelSx,
    width: COLUMN_WIDTH,
    minWidth: COLUMN_WIDTH,
    maxWidth: COLUMN_WIDTH,
  };
  const actionIconSx = {
    ...ui.actionIconSx,
  };
  const toSafeText = (value, fallback = "") => {
    if (value == null) return fallback;
    return typeof value === "string" ? value : String(value);
  };
  const safeDocumentos = Array.isArray(documentos) ? documentos : [];
  const folderCounts = countByFolder(safeDocumentos);
  const currentFolder = getFolderById(openFolder);
  const seasonFolders = uniqueSeasonFolders(safeDocumentos, temporadas)
    .slice()
    .sort((a, b) => {
      if (a.id === UNASSIGNED_SEASON) return 1;
      if (b.id === UNASSIGNED_SEASON) return -1;
      return compareSeasonNames(a.label, b.label, temporadas);
    });
  const extraSeasons = catalogSeasonsNotInFolders(temporadas, seasonFolders)
    .slice()
    .sort((a, b) => compareSeasonNames(a, b, temporadas));
  const visibleDocs = !openFolder
    ? []
    : openFolder === "Tendencias"
      ? openTemporada
        ? docsInSeason(safeDocumentos, openTemporada)
        : []
      : docsInFolder(safeDocumentos, openFolder);
  const canUpload =
    Boolean(openFolder) &&
    (openFolder !== "Tendencias" ||
      Boolean(openTemporada && openTemporada !== UNASSIGNED_SEASON));
  const seasonLabel =
    openTemporada === UNASSIGNED_SEASON
      ? t("Clientes.docsUnassignedSeason", { defaultValue: "Sin temporada" })
      : openTemporada;
  const formatSafeDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? "-"
      : parsed.toLocaleDateString("es-ES");
  };

  useEffect(() => {
    setOpenFolder(null);
    setOpenTemporada(null);
    setTipoDocumento("");
    setSeasonToOpen("");
    if (customerId) loadDocs();
  }, [customerId]);

  useEffect(() => {
    let cancelled = false;
    getTemporadas()
      .then((data) => {
        if (!cancelled) setTemporadas(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setTemporadas([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadOfficePreview = async () => {
      setOfficeBlob(null);
      setPreviewError("");
      setExcelSheets([]);
      setExcelSheetName("");
      setExcelHtml("");
      excelWorkbookRef.current = null;
      if (docxContainerRef.current) {
        docxContainerRef.current.innerHTML = "";
      }

      if (!previewDoc?.id_Documento) {
        setPreviewLoading(false);
        return;
      }
      if (previewDoc.type !== "docx" && previewDoc.type !== "excel") {
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);
      try {
        const blob = await fetchCustomerDocumentBlob(previewDoc.id_Documento);
        if (cancelled) return;

        if (previewDoc.type === "docx") {
          setOfficeBlob(blob);
        } else {
          const buffer = await blob.arrayBuffer();
          if (cancelled) return;
          const workbook = XLSX.read(buffer, { type: "array" });
          excelWorkbookRef.current = workbook;
          const sheets = workbook.SheetNames || [];
          setExcelSheets(sheets);
          const firstSheet = sheets[0] || "";
          setExcelSheetName(firstSheet);
          if (firstSheet && workbook.Sheets[firstSheet]) {
            setExcelHtml(
              XLSX.utils.sheet_to_html(workbook.Sheets[firstSheet], {
                id: "customer-excel-preview",
              })
            );
          }
          setOfficeBlob(null);
        }
      } catch (err) {
        console.error("Error previsualizando documento Office:", err);
        if (!cancelled) {
          setPreviewError(
            "No se pudo previsualizar este archivo. Puedes descargarlo."
          );
          setOfficeBlob(null);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    loadOfficePreview();

    return () => {
      cancelled = true;
    };
  }, [previewDoc]);

  useEffect(() => {
    let cancelled = false;

    const renderDocx = async () => {
      if (previewDoc?.type !== "docx" || !officeBlob) return;
      const container = docxContainerRef.current;
      if (!container) return;
      container.innerHTML = "";
      try {
        await renderDocxAsync(officeBlob, container, undefined, {
          className: "docx-preview-body",
          inWrapper: true,
          ignoreWidth: false,
          breakPages: true,
        });
      } catch (err) {
        console.error("Error renderizando DOCX:", err);
        if (!cancelled) {
          setPreviewError(
            "No se pudo previsualizar este archivo. Puedes descargarlo."
          );
        }
      }
    };

    renderDocx();
    return () => {
      cancelled = true;
    };
  }, [officeBlob, previewDoc?.type]);

  useEffect(() => {
    if (previewDoc?.type !== "excel") return;
    const workbook = excelWorkbookRef.current;
    if (!workbook || !excelSheetName || !workbook.Sheets[excelSheetName]) return;
    setExcelHtml(
      XLSX.utils.sheet_to_html(workbook.Sheets[excelSheetName], {
        id: "customer-excel-preview",
      })
    );
  }, [excelSheetName, previewDoc?.type]);

  const loadDocs = async () => {
    const data = await getCustomerDocuments(customerId);
    setDocumentos(Array.isArray(data) ? data : []);
  };

  const uploadFile = async (file) => {
    if (!file || isUploading) return;

    if (!tipoDocumento || !canUpload) {
      alert(
        tipoDocumento === "Tendencias"
          ? "Elige una temporada antes de subir el documento"
          : "Abre una carpeta antes de subir el documento",
      );
      return;
    }

    const docName = nombreDocumento.trim() || file.name;
    const temporada =
      tipoDocumento === "Tendencias" && openTemporada !== UNASSIGNED_SEASON
        ? openTemporada
        : "";

    setIsUploading(true);
    try {
      await uploadCustomerDocument(
        file,
        customerId,
        tipoDocumento,
        fechaCaducidad,
        docName,
        temporada,
      );

      setNombreDocumento("");
      await loadDocs();
    } catch (err) {
      console.error("Error subiendo documento:", err);
      alert("Error al subir el documento");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelected = (file) => {
    if (!file) return;
    if (!nombreDocumento.trim()) {
      setNombreDocumento(file.name);
    }
    uploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  const startDownload = async (id_Documento, downloadName) => {
    if (!id_Documento || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadCustomerDocument(id_Documento, downloadName);
    } catch (err) {
      console.error("Error descargando documento:", err);
      alert("Error al descargar el documento");
    } finally {
      setIsDownloading(false);
    }
  };

  const enterFolder = (folderId) => {
    setOpenFolder(folderId);
    setOpenTemporada(null);
    setTipoDocumento(getFolderById(folderId)?.value || "");
    setSeasonToOpen("");
  };

  const enterSeason = (seasonId) => {
    setOpenFolder("Tendencias");
    setOpenTemporada(seasonId);
    setTipoDocumento("Tendencias");
    setSeasonToOpen("");
  };

  const goBack = () => {
    if (openFolder === "Tendencias" && openTemporada) {
      setOpenTemporada(null);
      return;
    }
    setOpenFolder(null);
    setOpenTemporada(null);
    setTipoDocumento("");
    setSeasonToOpen("");
  };

  const renderFolderRow = (title, count, onClick, subtitle) => (
    <Box
      key={title}
      onClick={onClick}
      sx={{
        ...ui.cardSx,
        mb: 1,
        p: "0.7rem 0.85rem",
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          ...ui.avatarSx,
          borderRadius: 1.75,
          width: 40,
          height: 40,
        }}
      >
        <FolderOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={ui.cardTitleSx}>{title}</Typography>
        {subtitle ? (
          <Typography sx={{ ...ui.metaTextSx, mt: 0.15 }}>{subtitle}</Typography>
        ) : null}
      </Box>
      <Typography sx={{ ...ui.metaTextSx, fontWeight: 800 }}>{count}</Typography>
      <ChevronRightIcon sx={{ color: "text.secondary", fontSize: 20 }} />
    </Box>
  );

  const headerEyebrow = !openFolder
    ? t("Clientes.docsEyebrow", { defaultValue: "Documents" })
    : openFolder === "Tendencias" && openTemporada
      ? `Tendencias / ${seasonLabel}`
      : currentFolder?.label || "Documents";
  const headerCount = !openFolder
    ? safeDocumentos.length
    : openFolder === "Tendencias" && !openTemporada
      ? folderCounts.Tendencias || 0
      : visibleDocs.length;
  const uploadDestination =
    openFolder === "Tendencias"
      ? `Tendencias · ${seasonLabel}`
      : currentFolder?.label || "";

  return (
    /*  <Box sx={{ border: "1px solid #ccc", p: 2 }}> */
    <Box sx={panelSx}>
      {/* 🔹 Cabecera fija */}
      <Box sx={{ ...ui.headerSx, pl: openFolder ? 1 : 2 }}>
        {openFolder ? (
          <IconButton
            onClick={goBack}
            size="small"
            sx={{ ...actionIconSx, mr: 0.25, flexShrink: 0 }}
            title={t("Clientes.docsBack", { defaultValue: "Volver" })}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Typography sx={ui.headerEyebrowSx}>{headerEyebrow}</Typography>
          <Typography sx={ui.headerTitleSx}>
            {headerCount}{" "}
            {headerCount === 1 ? "documento" : "documentos"}
          </Typography>
        </Box>
      </Box>

      {/* Listado */}
      <Box sx={ui.bodySx}>
        {!openFolder ? (
          <>
            {CUSTOMER_DOC_FOLDERS.map((folder) =>
              renderFolderRow(
                folder.label,
                folderCounts[folder.id] || 0,
                () => enterFolder(folder.id),
                folder.id === "Tendencias"
                  ? t("Clientes.docsTrendsHint", {
                      defaultValue: "Organizadas por temporada",
                    })
                  : null,
              ),
            )}
            <Typography
              sx={{
                ...ui.metaTextSx,
                px: 0.5,
                pt: 0.5,
                fontWeight: 600,
              }}
            >
              {t("Clientes.docsPickFolder", {
                defaultValue: "Abre una carpeta para ver o subir documentos.",
              })}
            </Typography>
          </>
        ) : openFolder === "Tendencias" && !openTemporada ? (
          <>
            {seasonFolders.length === 0 ? (
              <Typography sx={{ ...ui.emptyStateSx, mb: 1.25 }}>
                {t("Clientes.docsNoSeasons", {
                  defaultValue:
                    "Aún no hay tendencias. Elige una temporada para crear su carpeta.",
                })}
              </Typography>
            ) : (
              seasonFolders.map((folder) =>
                renderFolderRow(
                  folder.label,
                  folder.count,
                  () => enterSeason(folder.id),
                ),
              )
            )}
            {extraSeasons.length > 0 ? (
              <FormControl
                size="small"
                fullWidth
                sx={{
                  mt: 0.5,
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  ...ui.fieldSx,
                }}
              >
                <InputLabel id="customer-docs-season-label">
                  {t("Clientes.docsOpenSeason", {
                    defaultValue: "Abrir temporada",
                  })}
                </InputLabel>
                <Select
                  labelId="customer-docs-season-label"
                  label={t("Clientes.docsOpenSeason", {
                    defaultValue: "Abrir temporada",
                  })}
                  displayEmpty
                  value={seasonToOpen}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSeasonToOpen(next);
                    if (next) enterSeason(next);
                  }}
                  sx={{ width: "100%" }}
                >
                  {extraSeasons.map((season) => (
                    <MenuItem key={season} value={season}>
                      {season}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {temporadas.length === 0 ? (
              <TextField
                size="small"
                fullWidth
                label={t("Clientes.docsSeason", { defaultValue: "Temporada" })}
                placeholder="SS26"
                value={seasonToOpen}
                onChange={(e) => setSeasonToOpen(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && seasonToOpen.trim()) {
                    enterSeason(seasonToOpen.trim());
                  }
                }}
                helperText={t("Clientes.docsSeasonHelper", {
                  defaultValue: "Escribe la temporada (SS26, FW26…) y pulsa Enter.",
                })}
                sx={{ mt: 0.5, width: "100%", ...ui.fieldSx }}
              />
            ) : null}
          </>
        ) : visibleDocs.length === 0 ? (
          canUpload ? null : (
            <Typography sx={ui.emptyStateSx}>
              {t("Clientes.noDocumentsInFolder", {
                defaultValue: "Esta carpeta está vacía.",
              })}
            </Typography>
          )
        ) : (
          visibleDocs.map((doc) => {
            const filePath = toSafeText(doc.Archivo);
            const displayName =
              toSafeText(doc.NombreDocumento) ||
              toSafeText(doc.nombreDocumento) ||
              filePath.split("/").pop() ||
              filePath ||
              "Documento";
            const fileLabel = (
              pickNameWithExtension(filePath, displayName).split(".").pop() ||
              "DOC"
            )
              .slice(0, 3)
              .toUpperCase();
            const fileHref = filePath
              ? `${process.env.REACT_APP_API_URL}/uploads/clientes/${filePath}`
              : "";
            const previewType = getPreviewType(filePath, displayName);
            const downloadName = resolveDownloadName(displayName, filePath);
            const canPreview = Boolean(fileHref || doc.id_Documento);
            const openPreview = () => {
              if (!canPreview) return;
              setPreviewDoc({
                url: fileHref,
                title: displayName,
                type: previewType,
                id_Documento: doc.id_Documento,
                downloadName,
              });
            };

            return (
              <Box
                key={doc.id_Documento}
                sx={{
                  ...ui.cardSx,
                  mb: 1,
                  p: "0.75rem",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 0.75,
                }}
              >
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                  <Box
                    onClick={canPreview ? openPreview : undefined}
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 1.75,
                      overflow: "hidden",
                      flexShrink: 0,
                      border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                      bgcolor: alpha(theme.palette.background.default, 0.65),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: canPreview ? "zoom-in" : "default",
                      transition: "box-shadow 0.15s ease, transform 0.15s ease",
                      "&:hover": canPreview
                        ? {
                            boxShadow: `0 4px 14px ${alpha(accent, 0.18)}`,
                            transform: "scale(1.03)",
                          }
                        : undefined,
                    }}
                  >
                    {!fileHref && !doc.id_Documento ? (
                      <Box sx={{ ...ui.avatarSx, fontSize: "0.7rem" }}>
                        {fileLabel}
                      </Box>
                    ) : previewType === "image" && fileHref ? (
                      <Box
                        component="img"
                        src={fileHref}
                        alt={displayName}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : previewType === "pdf" && fileHref ? (
                      <Box
                        component="iframe"
                        src={`${fileHref}#toolbar=0&navpanes=0&view=FitH`}
                        title={displayName}
                        sx={{
                          width: "100%",
                          height: "100%",
                          border: 0,
                          pointerEvents: "none",
                          bgcolor: "#fff",
                        }}
                      />
                    ) : (
                      <Box sx={{ ...ui.avatarSx, fontSize: "0.7rem" }}>
                        {fileLabel}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "text.primary",
                        fontSize: "0.86rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {displayName}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.45,
                        flexWrap: "wrap",
                        mt: 0.45,
                      }}
                    >
                      <Box sx={ui.pillSx}>
                        Exp: {formatSafeDate(doc.FechaCaducidad)}
                      </Box>
                      {canPreview ? (
                        <Button
                          variant="text"
                          size="small"
                          onClick={openPreview}
                          sx={{
                            minWidth: 0,
                            p: 0,
                            fontSize: "0.74rem",
                            textTransform: "none",
                            lineHeight: 1.2,
                          }}
                        >
                          Ver documento
                        </Button>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.74rem", color: "text.secondary" }}
                        >
                          Archivo no disponible
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    onClick={() =>
                      startDownload(doc.id_Documento, downloadName)
                    }
                    sx={{
                      ...actionIconSx,
                      borderColor: alpha(accent, 0.24),
                    }}
                    size="small"
                    title="Descargar"
                    disabled={isDownloading || !fileHref}
                  >
                    <DownloadOutlinedIcon fontSize="small" sx={{ color: accent }} />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setEditDoc({
                        id_Documento: doc.id_Documento,
                        nombreDocumento:
                          doc.NombreDocumento ||
                          doc.nombreDocumento ||
                          displayName,
                      })
                    }
                    sx={{
                      ...actionIconSx,
                      borderColor: alpha("#34afe2", 0.24),
                    }}
                    size="small"
                    title="Editar nombre"
                  >
                    <EditOutlinedIcon fontSize="small" sx={{ color: "#34afe2" }} />
                  </IconButton>
                  <IconButton
                    onClick={() => setConfirmDelete(doc.id_Documento)}
                    sx={{
                      ...actionIconSx,
                      borderColor: alpha(theme.palette.error.main, 0.24),
                    }}
                    size="small"
                  >
                    <DeleteOutlineOutlinedIcon sx={{ color: theme.palette.error.main }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}

        {openTemporada === UNASSIGNED_SEASON ? (
          <Typography sx={{ ...ui.metaTextSx, px: 0.5, pt: 0.75 }}>
            {t("Clientes.docsUnassignedHint", {
              defaultValue:
                "Documentos de tendencias sin temporada. Los nuevos hay que subirlos dentro de una temporada.",
            })}
          </Typography>
        ) : null}

        {canUpload ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            pt: 1.25,
            mt: visibleDocs.length ? 0.75 : 0,
            borderTop: visibleDocs.length
              ? `1px solid ${alpha(theme.palette.divider, 0.55)}`
              : "none",
            gap: 1.25,
            ...ui.fieldSx,
          }}
        >
        <Box sx={ui.pillSx}>
          {t("Clientes.docsUploadTo", { defaultValue: "Subir a" })}: {uploadDestination}
        </Box>

        <TextField
          size="small"
          label="Document name"
          placeholder="Name for this document"
          value={nombreDocumento}
          onChange={(e) => setNombreDocumento(e.target.value)}
          helperText="Optional; defaults to file name on upload"
        />

        <TextField
          size="small"
          type="date"
          label="Expiration Date"
          InputLabelProps={{ shrink: true }}
          value={fechaCaducidad}
          onChange={(e) => setFechaCaducidad(e.target.value)}
        />

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 2.5,
            border: "1px dashed",
            borderColor: isDragging ? accent : alpha(accent, 0.28),
            borderRadius: 2.5,
            textAlign: "center",
            transition: "0.2s",
            backgroundColor: isDragging
              ? alpha(accent, 0.08)
              : alpha(theme.palette.background.default, 0.45),
            cursor: isUploading ? "wait" : "pointer",
            opacity: isUploading ? 0.6 : 1,
            pointerEvents: isUploading ? "none" : "auto",
          }}
          onClick={() => !isUploading && document.getElementById("upload-doc").click()}
        >
          <input
            type="file"
            id="upload-doc"
            style={{ display: "none" }}
            disabled={isUploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileSelected(file);
                e.target.value = "";
              }
            }}
          />

          <Typography sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
            {isUploading
              ? "Uploading..."
              : isDragging
                ? "Drop the file here"
                : "Drag & drop or click to upload"}
          </Typography>

          <UploadFile sx={{ fontSize: 32, mt: 0.75, color: accent, opacity: 0.85 }} />
        </Box>
        </Box>
        ) : null}
      </Box>

      <Dialog
        open={!!editDoc}
        onClose={() => setEditDoc(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar nombre del documento</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre del documento"
            value={editDoc?.nombreDocumento ?? ""}
            onChange={(e) =>
              setEditDoc((prev) =>
                prev ? { ...prev, nombreDocumento: e.target.value } : null,
              )
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDoc(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editDoc?.id_Documento || !editDoc?.nombreDocumento?.trim())
                return;
              await updateCustomerDocumentName(
                editDoc.id_Documento,
                editDoc.nombreDocumento.trim(),
              );
              loadDocs();
              setEditDoc(null);
            }}
            disabled={!editDoc?.nombreDocumento?.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
        maxWidth={
          previewDoc?.type === "pdf" ||
          previewDoc?.type === "docx" ||
          previewDoc?.type === "excel"
            ? "lg"
            : "md"
        }
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0,0,0,0.82)" } },
        }}
        PaperProps={{
          sx: {
            bgcolor: isDocumentPreview(previewDoc?.type)
              ? "background.paper"
              : "rgba(18,18,18,0.98)",
            backgroundImage: "none",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1.25,
            px: 2,
            color: isDocumentPreview(previewDoc?.type)
              ? "text.primary"
              : "common.white",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "0.92rem",
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {previewDoc?.title}
          </Typography>
          <IconButton
            onClick={() => setPreviewDoc(null)}
            aria-label="Cerrar vista previa"
            size="small"
            sx={{
              color: isDocumentPreview(previewDoc?.type)
                ? "text.secondary"
                : "common.white",
              bgcolor: isDocumentPreview(previewDoc?.type)
                ? alpha(theme.palette.text.primary, 0.06)
                : "rgba(255,255,255,0.14)",
              "&:hover": {
                bgcolor: isDocumentPreview(previewDoc?.type)
                  ? alpha(theme.palette.text.primary, 0.1)
                  : "rgba(255,255,255,0.24)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: {
              xs: 1,
              sm: isDocumentPreview(previewDoc?.type) ? 1 : 2,
            },
            pt: 0,
            textAlign: previewDoc?.type === "image" ? "center" : "left",
          }}
        >
          {previewDoc?.type === "image" ? (
            <Box
              component="img"
              src={previewDoc.url}
              alt={previewDoc.title}
              sx={{
                maxWidth: "100%",
                maxHeight: "min(78vh, 920px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                display: "inline-block",
              }}
            />
          ) : previewDoc?.type === "pdf" ? (
            <Box
              component="iframe"
              src={previewDoc.url}
              title={previewDoc.title}
              sx={{
                width: "100%",
                height: "min(78vh, 900px)",
                border: "none",
                display: "block",
                borderRadius: 1,
                bgcolor: "#fff",
              }}
            />
          ) : previewDoc?.type === "docx" || previewDoc?.type === "excel" ? (
            <Box sx={{ position: "relative", minHeight: 240 }}>
              {previewDoc.type === "excel" && excelSheets.length > 1 ? (
                <FormControl size="small" sx={{ mb: 1, minWidth: 180 }}>
                  <InputLabel id="excel-sheet-label">Hoja</InputLabel>
                  <Select
                    labelId="excel-sheet-label"
                    label="Hoja"
                    value={excelSheetName}
                    onChange={(e) => setExcelSheetName(e.target.value)}
                    disabled={previewLoading}
                  >
                    {excelSheets.map((sheet) => (
                      <MenuItem key={sheet} value={sheet}>
                        {sheet}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              {previewLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 240,
                  }}
                >
                  <CircularProgress size={36} />
                </Box>
              ) : null}
              {previewError && !previewLoading ? (
                <DialogContentText sx={{ py: 3, px: 1, color: "text.secondary" }}>
                  {previewError}
                </DialogContentText>
              ) : null}
              <Box
                ref={docxContainerRef}
                sx={{
                  display:
                    previewDoc.type === "docx" && !previewLoading && !previewError
                      ? "block"
                      : "none",
                  maxHeight: "min(78vh, 900px)",
                  overflow: "auto",
                  bgcolor: "#fff",
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  p: 1,
                  "& .docx-wrapper": {
                    background: "#fff",
                    padding: "8px",
                  },
                  "& section.docx": {
                    boxShadow: "none",
                    marginBottom: "12px",
                  },
                }}
              />
              {previewDoc.type === "excel" && !previewLoading && !previewError ? (
                <Box
                  sx={{
                    maxHeight: "min(78vh, 900px)",
                    overflow: "auto",
                    bgcolor: "#fff",
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                    p: 1,
                    "& table": {
                      borderCollapse: "collapse",
                      width: "max-content",
                      minWidth: "100%",
                      fontSize: "0.78rem",
                    },
                    "& td, & th": {
                      border: "1px solid #d0d0d0",
                      padding: "4px 8px",
                      whiteSpace: "nowrap",
                    },
                    "& th": {
                      bgcolor: "#f3f3f3",
                      fontWeight: 700,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: excelHtml }}
                />
              ) : null}
            </Box>
          ) : previewDoc?.type === "other" ? (
            <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
              <Box sx={{ ...ui.avatarSx, fontSize: "0.95rem", mx: "auto", mb: 1.5 }}>
                {(previewDoc.title.split(".").pop() || "DOC").slice(0, 3).toUpperCase()}
              </Box>
              <DialogContentText sx={{ color: "grey.300" }}>
                Este tipo de archivo no se puede previsualizar en el navegador.
                Si lo necesitas, puedes descargarlo (te pediremos confirmación).
              </DialogContentText>
            </Box>
          ) : null}
        </DialogContent>
        {previewDoc?.url || previewDoc?.id_Documento ? (
          <DialogActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
            {previewDoc.type === "other" ? (
              <Button
                startIcon={<DownloadOutlinedIcon />}
                size="small"
                sx={{ textTransform: "none" }}
                disabled={isDownloading}
                onClick={() =>
                  setConfirmDownload({
                    id_Documento: previewDoc.id_Documento,
                    downloadName: previewDoc.downloadName,
                    title: previewDoc.title,
                  })
                }
              >
                Descargar archivo
              </Button>
            ) : (
              <>
                <Button
                  startIcon={<DownloadOutlinedIcon />}
                  size="small"
                  sx={{ textTransform: "none" }}
                  disabled={isDownloading}
                  onClick={() =>
                    startDownload(
                      previewDoc.id_Documento,
                      previewDoc.downloadName || previewDoc.title,
                    )
                  }
                >
                  Descargar
                </Button>
                {previewDoc.url ? (
                  <Button
                    component="a"
                    href={previewDoc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewOutlinedIcon />}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Abrir en nueva pestaña
                  </Button>
                ) : null}
              </>
            )}
          </DialogActions>
        ) : null}
      </Dialog>

      <Dialog
        open={Boolean(confirmDownload)}
        onClose={() => setConfirmDownload(null)}
      >
        <DialogTitle>Descargar documento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Quieres descargar{" "}
            <strong>{confirmDownload?.title || "este archivo"}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDownload(null)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            disabled={isDownloading}
            onClick={async () => {
              if (!confirmDownload?.id_Documento) return;
              await startDownload(
                confirmDownload.id_Documento,
                confirmDownload.downloadName || confirmDownload.title,
              );
              setConfirmDownload(null);
            }}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que quieres eliminar este documento?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={async () => {
              await deleteCustomerDocument(confirmDelete);
              loadDocs();
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDocs;
